/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the phone workspace title, teacher workspace selection, and compact inbox and account actions.
 * @state-owner overlay host owns anchored menus; parent owns full-page destinations
 * @allowed-imports React; app providers; auth/notification module public APIs; shared components; sibling help popover; Agent A workspace selection composition
 * @forbidden desktop rail; modal settings; direct API client
 * @public-api MobileWorkspaceShell
 * @side-effects opens anchored menus, changes teacher workspace URL, navigates to mobile pages, signs out
 * @tests frontend/apps/web/e2e/workspace.spec.ts
 */

import { type MouseEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileWorkspaceSwitcher } from '../../app/feature-composition/agent-a/WorkspaceSwitcher';
import { WorkspaceSectionActions } from '../../app/feature-composition/WorkspaceSectionActions';
import { useActiveContextId, useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../app/providers/SessionProvider';
import { useSignOut } from '../../modules/auth';
import { useInbox } from '../../modules/notifications';
import { anchorPoint, useOverlay } from '../../shared/components/OverlayHost';
import { PopoverIcon } from '../../shared/components/VispoIcon';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { HelpPopover } from './ShellPopovers';

export type MobileUtilityPage = 'inbox' | 'profile' | 'notifications';

export function MobileTopBar({
  title,
  onOpenPage,
  onOpenSettings,
  showBadgeCount,
}: {
  title: string;
  onOpenPage(page: MobileUtilityPage): void;
  onOpenSettings(): void;
  showBadgeCount: boolean;
}) {
  const t = useTranslator();
  const role = useActiveRole();
  const contextId = useActiveContextId();
  const { session } = useWorkspaceSession();
  const inbox = useInbox(contextId, session?.portalAudience, true);
  // Server unreadCount verbatim -- never recomputed from the bounded `items` array, which is
  // capped at the list's `limit` and would silently under-report once unread rows exceed it.
  const unread = inbox.data?.unreadCount ?? 0;
  const accountLabel = session?.displayName || session?.email || t('帳號');
  const { openPopover } = useOverlay();

  const open = (event: MouseEvent<HTMLButtonElement>, content: ReactNode, className: string) => {
    event.stopPropagation();
    const point = anchorPoint(event);
    openPopover({ content, x: point.x, y: point.y, className });
  };

  return (
    <header
      className={`mobile-workspace-header mobile-top-bar${
        role === 'teacher' ? ' mobile-top-bar--workspace-switcher' : ''
      }`}
    >
      <MobileWorkspaceSwitcher />
      <h1 id="mobile-workspace-title">{title}</h1>
      <div className="mobile-top-bar__actions">
        <WorkspaceSectionActions surface="mobile-list" />
        <button
          aria-label={unread > 0 ? `${t('收件匣')}，${unread} ${t('則未讀')}` : t('收件匣')}
          className="mobile-top-bar__icon"
          type="button"
          onClick={() => onOpenPage('inbox')}
        >
          <PopoverIcon name="mail" />
          {showBadgeCount && unread > 0 ? (
            <span aria-hidden="true" className="mobile-top-bar__badge">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
        <button
          aria-label={accountLabel}
          className="mobile-top-bar__avatar"
          type="button"
          onClick={(event) =>
            open(
              event,
              <MobileAccountMenu onOpenPage={onOpenPage} onOpenSettings={onOpenSettings} />,
              'mobile-anchor-menu mobile-account-menu',
            )
          }
        >
          {session?.avatarUrl ? (
            <img alt="" src={session.avatarUrl} />
          ) : (
            firstGrapheme(accountLabel)
          )}
        </button>
      </div>
    </header>
  );
}

function MobileAccountMenu({
  onOpenPage,
  onOpenSettings,
}: {
  onOpenPage(page: MobileUtilityPage): void;
  onOpenSettings(): void;
}) {
  const role = useActiveRole();
  const { session } = useWorkspaceSession();
  const t = useTranslator();
  const { closePopover, openPopover } = useOverlay();
  const signOut = useSignOut();
  const [signOutFailed, setSignOutFailed] = useState(false);
  const navigate = useNavigate();
  const accountLabel = session?.displayName || session?.email || t('帳號');

  const notify = useWorkspaceUiStore((state) => state.notify);
  const attemptSignOut = () => {
    setSignOutFailed(false);
    signOut.mutate(undefined, {
      onSuccess: () => {
        closePopover();
        notify(t('已登出'));
      },
      onError: () => setSignOutFailed(true),
    });
  };

  const openPage = (page: MobileUtilityPage) => {
    closePopover();
    onOpenPage(page);
  };

  const openHelp = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const point = anchorPoint(event);
    openPopover({
      content: <HelpPopover />,
      x: point.x,
      y: point.y,
      className: 'mobile-anchor-menu mobile-help-menu',
    });
  };

  return (
    <div className="mobile-account-menu__content">
      <div className="mobile-account-menu__identity">
        <span className="mobile-account-menu__avatar">
          {session?.avatarUrl ? (
            <img alt="" src={session.avatarUrl} />
          ) : (
            firstGrapheme(accountLabel)
          )}
        </span>
        <span>
          <b>{accountLabel}</b>
          <small>{t(role === 'teacher' ? '老師' : '學生')}</small>
        </span>
      </div>
      <div className="mobile-account-menu__rows">
        {role !== 'teacher' ? (
          <MenuRow
            icon="user"
            label={t('設定')}
            onSelect={() => {
              closePopover();
              navigate('/student/settings');
            }}
          />
        ) : null}
        {role === 'teacher' ? (
          <MenuRow
            icon="gear"
            label={t('設定')}
            onSelect={() => {
              closePopover();
              onOpenSettings();
            }}
          />
        ) : null}
        <MenuRow icon="bell" label={t('通知設定')} onSelect={() => openPage('notifications')} />
        <button className="mobile-account-menu__row" type="button" onClick={openHelp}>
          <PopoverIcon name="help" />
          {t('需要協助？')}
        </button>
      </div>
      <button
        className="mobile-account-menu__row mobile-account-menu__logout"
        disabled={signOut.isPending}
        type="button"
        onClick={attemptSignOut}
      >
        <PopoverIcon name="out" />
        {t('登出')}
      </button>
      {signOutFailed ? (
        <div
          className="mobile-account-menu__row"
          role="alert"
          style={{ color: 'var(--error)', fontSize: 13 }}
        >
          {t('登出失敗，請重試')}
          <button className="mobile-account-menu__row" type="button" onClick={attemptSignOut}>
            {t('重試')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onSelect,
}: {
  icon: 'user' | 'gear' | 'bell';
  label: string;
  onSelect(): void;
}) {
  return (
    <button className="mobile-account-menu__row" type="button" onClick={onSelect}>
      <PopoverIcon name={icon} />
      {label}
    </button>
  );
}

function firstGrapheme(value: string): string {
  return Array.from(value.trim())[0]?.toLocaleUpperCase() || 'V';
}
